import React from 'react';
import { User, Phone, MessageCircle, Mail, StickyNote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getStageColor } from './CustomerList';

export const CustomerDetailModal = ({ 
  open, onOpenChange, customer, newNote, setNewNote, onAddNote 
}) => {
  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-detail-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Customer Details</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-[#001A47] flex items-center justify-center">
              <User className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold">{customer.name}</h3>
              <p className="text-gray-500">{customer.mobile}</p>
              {customer.email && <p className="text-gray-500">{customer.email}</p>}
              <Badge className={`mt-2 ${getStageColor(customer.lifecycle_stage)}`}>
                {customer.lifecycle_stage}
              </Badge>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <a href={`tel:${customer.mobile}`}>
                <Phone className="h-4 w-4 mr-2" />Call
              </a>
            </Button>
            <Button variant="outline" className="bg-green-50 text-green-700" asChild>
              <a href={`https://wa.me/${customer.mobile?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-4 w-4 mr-2" />WhatsApp
              </a>
            </Button>
            {customer.email && (
              <Button variant="outline" asChild>
                <a href={`mailto:${customer.email}`}>
                  <Mail className="h-4 w-4 mr-2" />Email
                </a>
              </Button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-[#001A47]">{customer.total_orders || 0}</p>
              <p className="text-sm text-gray-500">Total Orders</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">R{customer.total_spent || 0}</p>
              <p className="text-sm text-gray-500">Total Spent</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{customer.lead_score || 0}</p>
              <p className="text-sm text-gray-500">Lead Score</p>
            </div>
          </div>

          {/* Notes */}
          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <StickyNote className="h-4 w-4" />Notes
            </h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {customer.notes?.length > 0 ? (
                customer.notes.map((note, i) => (
                  <div key={`${note.created_at}-${i}`} className="bg-yellow-50 p-3 rounded text-sm">
                    <p>{note.content}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(note.created_at).toLocaleString()} - {note.created_by}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No notes yet</p>
              )}
            </div>
            <div className="mt-3 flex gap-2">
              <Input placeholder="Add a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} />
              <Button onClick={onAddNote}>Add</Button>
            </div>
          </div>

          {/* Recent Orders */}
          {customer.orders?.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Recent Orders</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {customer.orders.slice(0, 5).map((order, i) => (
                  <div key={order.quote_id || order.application_id || `order-${i}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <p className="font-mono text-sm">{order.quote_id || order.application_id}</p>
                      <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <Badge>{order.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
